import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Cookie, X } from "lucide-react";
import {
  applyConsent,
  getStoredConsent,
  hasDecided,
  rehydrateConsent,
} from "@/lib/analytics/consent";
import { useAuth } from "@/contexts/AuthContext";
import { useUserRoles } from "@/hooks/useUserRoles";

/**
 * GDPR / CPRA / PIPEDA-friendly cookie consent banner.
 *
 *  - Renders nothing once the user has made a choice.
 *  - Re-opens when anything dispatches `gtp:open-consent-settings`
 *    (e.g. the "Cookie Settings" link in the footer).
 *  - Forces `ads=false` for kid roles regardless of UI toggle.
 */
const CookieConsentBanner = () => {
  const { user } = useAuth();
  const { primaryRole } = useUserRoles();

  // Treat unknown role or students as kids → no personalized ads ever.
  const kidLock = !user || primaryRole === "student";

  const [open, setOpen] = useState<boolean>(false);
  const [showCustom, setShowCustom] = useState(false);
  const [analytics, setAnalytics] = useState(true);
  const [ads, setAds] = useState(false);

  // First mount: rehydrate any prior choice into gtag, then decide whether
  // to show the banner.
  useEffect(() => {
    rehydrateConsent({ kidLock });
    if (!hasDecided()) {
      setOpen(true);
    } else {
      const stored = getStoredConsent();
      if (stored) {
        setAnalytics(stored.analytics);
        setAds(stored.ads);
      }
    }
  }, [kidLock]);

  // Listen for "open settings" requests from the footer.
  useEffect(() => {
    const handler = () => {
      const stored = getStoredConsent();
      if (stored) {
        setAnalytics(stored.analytics);
        setAds(stored.ads);
      }
      setShowCustom(true);
      setOpen(true);
    };
    window.addEventListener("gtp:open-consent-settings", handler);
    return () => window.removeEventListener("gtp:open-consent-settings", handler);
  }, []);

  if (!open) return null;

  const acceptAll = () => {
    applyConsent({ analytics: true, ads: true }, { kidLock });
    setOpen(false);
    setShowCustom(false);
  };

  const rejectAll = () => {
    applyConsent({ analytics: false, ads: false }, { kidLock });
    setOpen(false);
    setShowCustom(false);
  };

  const saveCustom = () => {
    applyConsent({ analytics, ads }, { kidLock });
    setOpen(false);
    setShowCustom(false);
  };

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
      className="fixed inset-x-0 bottom-0 z-[60] p-3 sm:p-4 pointer-events-none"
    >
      <div className="pointer-events-auto mx-auto max-w-3xl rounded-2xl border border-border bg-background/95 backdrop-blur shadow-2xl">
        <div className="p-4 sm:p-5">
          <div className="flex items-start gap-3">
            <div className="shrink-0 mt-0.5 rounded-full bg-primary/10 p-2 text-primary">
              <Cookie className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-base sm:text-lg font-semibold text-foreground">
                Your privacy choices
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                We use cookies to keep you signed in (always required) and — only
                with your permission — to measure how the app is used and to show
                relevant ads. You can change this any time from the footer.
                {kidLock && (
                  <>
                    {" "}
                    <span className="text-foreground font-medium">
                      Personalized ads are always off for student accounts.
                    </span>
                  </>
                )}
              </p>

              {showCustom && (
                <div className="mt-4 space-y-3 rounded-lg border border-border bg-muted/40 p-3">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="text-sm font-medium">Strictly necessary</div>
                      <div className="text-xs text-muted-foreground">
                        Required for sign-in and security. Always on.
                      </div>
                    </div>
                    <Switch checked disabled aria-label="Strictly necessary cookies (required)" />
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="text-sm font-medium">Analytics</div>
                      <div className="text-xs text-muted-foreground">
                        Anonymous usage stats (Google Analytics) so we can improve the app.
                      </div>
                    </div>
                    <Switch
                      checked={analytics}
                      onCheckedChange={setAnalytics}
                      aria-label="Allow analytics cookies"
                    />
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="text-sm font-medium">
                        Advertising{" "}
                        {kidLock && (
                          <span className="text-xs font-normal text-muted-foreground">
                            (disabled for students)
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Personalized ads via Google AdSense.
                      </div>
                    </div>
                    <Switch
                      checked={!kidLock && ads}
                      disabled={kidLock}
                      onCheckedChange={setAds}
                      aria-label="Allow advertising cookies"
                    />
                  </div>
                </div>
              )}

              <div className="mt-4 flex flex-wrap gap-2 sm:justify-end">
                {!showCustom && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowCustom(true)}
                  >
                    Customize
                  </Button>
                )}
                <Button variant="outline" size="sm" onClick={rejectAll}>
                  Reject all
                </Button>
                {showCustom ? (
                  <Button size="sm" onClick={saveCustom}>
                    Save preferences
                  </Button>
                ) : (
                  <Button size="sm" onClick={acceptAll}>
                    Accept all
                  </Button>
                )}
              </div>
            </div>

            <button
              type="button"
              onClick={rejectAll}
              aria-label="Close and reject non-essential cookies"
              className="shrink-0 rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsentBanner;
